import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { PlanDescriptor, appConfig } from '@common/config/app.config';
import { activeSubscriptionSelector, selectUserObject } from '@common-reducers/UserSelectors';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { useGetEntitlementsMutation, useGetItemPricesMutation, useGetPlanMapMutation } from '@common-reducers/backend-api/backend-api';
import { BillingPeriod } from '@common-components/subscription-selection/subscription-selection';
import { resolvePlanCta, PlanCtaDecision } from '../plan-selection/plan-cta';

/**
 * Data for the plans page (/account-subscription). Same sources and the same decisions as the
 * previous <PlanSelection /> (still served at /account-subscription-test for comparison) —
 * Chargebee item prices (per-seat tiers), the plan map, the entitlements per plan and
 * resolvePlanCta for Upgrade / Current / Update / Downgrade — so both price and gate identically.
 */

export type FeatureId = 'unlimited-messages' | 'unlimited-tasks' | 'remove-branding' | 'offline-socket';
export const FEATURE_ORDER: FeatureId[] = ['unlimited-messages', 'unlimited-tasks', 'remove-branding', 'offline-socket'];

/** App plan key → Chargebee item family prefix (e.g. "Basic-USD-Monthly"). */
const CB_TYPE: Record<string, string> = { Free: 'Free', Scheduler: 'Basic', Manager: 'StandardNew', Campaigner: 'Pro' };

interface ItemPrice {
  id: string;
  tiers?: Array<{ starting_unit: number; ending_unit?: number; price: number }>;
}

export interface PlanView {
  key: string;
  name: string;
  planKey: string;
  isFree: boolean;
  features: Set<FeatureId>;
  /** Per user per month, in dollars, for the selected seat count. */
  monthly: number;
  /** Per user per month when billed yearly (yearly price / 12). */
  yearlyPerMonth: number;
  cta: PlanCtaDecision;
  /** Catalogue tier rank for the selected seats/interval (may be absent). */
  order?: number;
  /** The marketing pick when the user has no paid plan yet. */
  recommended: boolean;
}

export const SEAT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 25, 30, 40, 50];

export function usePlanData() {
  const { t } = useTranslation(namespace);
  const user = useAppSelector(selectUserObject);
  const activeSubscription = useAppSelector(activeSubscriptionSelector);
  const [getPlanMap, { isLoading: loadingMap, data: planMap }] = useGetPlanMapMutation();
  const [getItemPrices, { isLoading: loadingPrices, data: itemPrices }] = useGetItemPricesMutation();
  const [getEntitlements, { data: entitlements }] = useGetEntitlementsMutation();

  useEffect(() => {
    if (!user) return;
    getPlanMap();
    getItemPrices();
    getEntitlements();
  }, [user, getPlanMap, getItemPrices, getEntitlements]);

  const [periodChoice, setPeriod] = useState<BillingPeriod | undefined>();
  const [seatChoice, setSeats] = useState<number | undefined>();
  const period: BillingPeriod = periodChoice ?? activeSubscription?.billingPeriod ?? 'year';
  const seats = seatChoice ?? activeSubscription?.seatCount ?? 1;

  const plans: PlanView[] = useMemo(() => {
    const basePlans = appConfig.pricingOptions === 'extension'
      ? t('planSelection.extensionPricing', { returnObjects: true })
      : t('planSelection.taskPricing', { returnObjects: true });
    if (!Array.isArray(basePlans)) return [];
    const prices = (itemPrices as unknown as ItemPrice[] | undefined) ?? [];

    const perUserMonth = (cbType: string, unit: 'Monthly' | 'Yearly') => {
      const item = prices.find(p => p.id.split('-')[0] === cbType && p.id.includes(unit));
      const tier = item?.tiers?.find(tr => seats >= tr.starting_unit && (!tr.ending_unit || seats <= tr.ending_unit));
      const dollars = (tier?.price ?? 0) / 100;
      return Number((unit === 'Yearly' ? dollars / 12 : dollars).toFixed(1));
    };

    // The plan descriptor for this seat count + interval — what the Upgrade/Downgrade decision compares.
    const descriptorFor = (key: string) => Object.values((planMap ?? {}) as Record<string, PlanDescriptor>)
      .find(p => p.planType === key && p.seatCount === seats && p.intervalUnit === period && p.bluesnapType === 'plan');

    return (basePlans as Array<{ key: string; name: string; planKey: string }>).map(base => {
      const cbType = CB_TYPE[base.key] ?? base.key;
      const ents = ((entitlements ?? {}) as Record<string, Array<{ feature_id: string }>>)[base.planKey] ?? [];
      const features = new Set(ents.map(e => e.feature_id).filter((f): f is FeatureId => FEATURE_ORDER.includes(f as FeatureId)));
      const d = descriptorFor(base.key);
      return {
        key: base.key,
        name: base.name,
        planKey: base.planKey,
        isFree: base.key === 'Free',
        features,
        monthly: perUserMonth(cbType, 'Monthly'),
        yearlyPerMonth: perUserMonth(cbType, 'Yearly'),
        cta: resolvePlanCta({ order: d?.order, seatCount: d?.seatCount, intervalUnit: d?.intervalUnit, isFreePlan: base.key === 'Free' }, activeSubscription),
        order: d?.order,
        recommended: cbType === 'StandardNew',
      };
    });
  }, [t, itemPrices, planMap, entitlements, seats, period, activeSubscription]);

  // The real yearly saving, from the prices — never a hard-coded "-20%".
  const yearlySavingPct = useMemo(() => {
    const pcts = plans.filter(p => !p.isFree && p.monthly > 0 && p.yearlyPerMonth > 0)
      .map(p => Math.round((1 - p.yearlyPerMonth / p.monthly) * 100));
    return pcts.length ? Math.max(...pcts) : 0;
  }, [plans]);

  return {
    user,
    activeSubscription,
    hasPaidSubscription: (activeSubscription?.order ?? 0) > 0,
    loading: loadingMap || loadingPrices || !itemPrices,
    plans,
    period,
    setPeriod,
    seats,
    setSeats,
    yearlySavingPct,
  };
}
